import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { formatDate, Mono, StatusChip, STATUS_META, ToneChip } from './consoleUi';

/** Status display order (worst first). */
const ORDER = ['fail', 'error', 'warn', 'info', 'pass'];

/**
 * Isolation tab: live tenant-bleed health checks, worst first.
 * @param {object} props - Props.
 * @param {{generatedAt?: string, checks: object[]}} props.data - isolation_checks payload.
 * @param {boolean} [props.refreshing] - A re-run is in flight.
 * @param {() => void} props.onRefresh - Run the checks again.
 * @returns {JSX.Element} Panel.
 */
export default function ConsoleIsolation({ data, refreshing = false, onRefresh }) {
  const checks = [...(data?.checks || [])].sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status));
  const totals = ORDER.map((status) => [status, checks.filter((c) => c.status === status).length]).filter(([, n]) => n > 0);
  const generated = data?.generatedAt ? new Date(data.generatedAt) : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, flex: '1 1 auto' }} aria-label="Summary">
          {totals.map(([status, n]) => (
            <ToneChip key={status} tone={STATUS_META[status]?.tone}>
              {`${n} ${STATUS_META[status]?.label.toLowerCase()}`}
            </ToneChip>
          ))}
        </Box>
        {generated && !Number.isNaN(generated.getTime()) && (
          <Typography variant="body2" sx={{ color: 'var(--q-muted, #5E5A54)' }}>
            {`Ran ${formatDate(data.generatedAt)} at ${generated.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
          </Typography>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={<Refresh fontSize="small" />}
          onClick={onRefresh}
          disabled={refreshing}
          sx={{ textTransform: 'none', borderRadius: 'var(--q-radius-pill, 999px)' }}
        >
          {refreshing ? 'Running' : 'Run again'}
        </Button>
      </Box>

      <Box
        component="ul"
        aria-label="Isolation checks"
        sx={{ listStyle: 'none', m: 0, p: 0, border: 1, borderColor: 'divider', borderRadius: 'var(--q-radius-card, 16px)', bgcolor: 'background.paper', overflow: 'hidden' }}
      >
        {checks.map((item, index) => (
          <Box
            component="li"
            key={item.id}
            data-testid={`check-${item.id}`}
            sx={{
              display: 'flex',
              gap: 1.5,
              alignItems: 'flex-start',
              px: { xs: 1.5, sm: 2 },
              py: 1.5,
              borderTop: index ? '1px solid var(--q-row-divider, #F0EDE8)' : 0,
              minWidth: 0,
            }}
          >
            <Box sx={{ pt: 0.25, width: 64, flex: 'none' }}>
              <StatusChip status={item.status} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1 }}>
                <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, '&::first-letter': { textTransform: 'uppercase' } }}>
                  {item.title}
                </Typography>
                {item.count > 0 && (
                  <Typography component="span" sx={{ fontSize: 12.5, color: 'var(--q-muted, #5E5A54)', fontVariantNumeric: 'tabular-nums' }}>
                    {`${item.count} found`}
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" sx={{ color: 'var(--q-muted, #5E5A54)', mt: 0.25 }}>
                {item.description}
              </Typography>
              {item.detail && (
                <Typography variant="body2" sx={{ color: 'var(--q-text-2, #3D3A35)', mt: 0.5 }}>
                  {item.detail}
                </Typography>
              )}
              {item.samples?.length > 0 && (
                <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.25 }} aria-label={`Samples for ${item.title}`}>
                  {item.samples.map((sample) => (
                    <Mono key={sample}>{sample}</Mono>
                  ))}
                  {item.count > item.samples.length && (
                    <Typography component="span" sx={{ fontSize: 12, color: 'var(--q-faint, #716C65)' }}>
                      {`and ${item.count - item.samples.length} more`}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
